
import { Injectable } from '@angular/core';
import ExcelJS from 'exceljs';
import { WebService, lastValueFrom } from '@services';

type BagNum = 1 | 2 | 3 | 4 | 5 | 6;

type InsectOption = InboundTallyData['InsectActivity'][number];
type ConditionOption = InboundTallyData['ConditionOnArrival'][number];



/** Convert a Row.values entry or a Cell-like object to visible text */
export interface InboundTallyData {
  FullPalletWeights: Array<{ WeightWithPallets: number; Bags: number }>;
  SlackWeights: Array<{ GrossWeight: number; Bags: number }>;
  TestWeights: Array<{ TestWeight: number; BagNumber: BagNum }>;

  LocationNumber: any;
  Comments?: string;
  DateStripped: string;  // e.g., '2026-01-14' or '01/14/2026'
  RefPO: string;
  CoffeeName: string;
  Marks: string;
  BagsExpected: number;
  ContainerNumber: string;
  SealNumber: string;

  ConditionOnArrival: Array<'Condensation' | 'Paper Lining' | 'HoleInContainer' | 'Other'>;
  InsectActivity: Array<'Larvae' | 'Beetles' | 'Moths' | 'Other'>;

  PalletWeight: number;
  TareWeight: Array<{ TareWeight: number; BagNumber: 1 | 2 | 3 }>;
  Weigher: string;
  Signature: string;
  HiLoDriver: string;
  Helper1: string;
  Helper2: string;
  StackingDriver: string;
}



@Injectable({ providedIn: 'root' })
export class ExcelWriterService {
  constructor(private webService: WebService) { }
  private EXCEL_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

  // ======================================================
  // WRITE (existing)
  // ======================================================
  async FillInboundTallyTemplate(tally: InboundTallyData, getPreview: boolean = false): Promise<Uint8Array | string> {
    const templatePath = 'TallyTemplate/Tally_Template.xlsx';

    // 1) Download template from your Node API as Blob
    const blob = await lastValueFrom(
      this.webService.getWebCall('files', 'download', {
        params: [{ name: 'path', value: templatePath }],
        responseType: 'blob'
      })
    );

    const ab = await blob.arrayBuffer();

    // 2) Load with ExcelJS
    const wb = new ExcelJS.Workbook();
    await wb.xlsx.load(ab);

    // Use the first sheet of the uploaded template
    const ws = wb.worksheets[0];

    // ----------------------------
    // Header fields (right-of-label)
    // ----------------------------
    this.setRightOfLabel(ws, 'Date Stripped', this.asDateValue(tally.DateStripped), { dateFmt: 'mm/dd/yyyy' });
    this.setRightOfLabel(ws, 'Ref#/PO#', tally.RefPO);
    this.setRightOfLabel(ws, 'Coffee Description', tally.CoffeeName);
    this.setRightOfLabel(ws, 'Marks', tally.Marks);
    this.setRightOfLabel(ws, 'Bags Expected', tally.BagsExpected);
    this.setRightOfLabel(ws, 'Container#', tally.ContainerNumber);
    this.setRightOfLabel(ws, 'Seal#', tally.SealNumber);

    this.setRightOfLabel(ws, 'Weigher:', tally.Weigher, { allowColonVariant: true });
    this.setRightOfLabel(ws, 'Signature:', tally.Signature, { allowColonVariant: true });
    this.setRightOfLabel(ws, 'HiLo Driver:', tally.HiLoDriver, { allowColonVariant: true });
    this.setRightOfLabel(ws, 'Helper #1', tally.Helper1);
    this.setRightOfLabel(ws, 'Helper #2', tally.Helper2);
    this.setRightOfLabel(ws, 'Stacking Driver:', tally.StackingDriver, { allowColonVariant: true });
    this.setRightOfLabel(ws, 'Location #:', tally.LocationNumber, { allowColonVariant: true });
    this.setRightOfLabel(ws, 'Comments:', tally.Comments ?? '', { allowColonVariant: true });
    this.setRightOfLabel(ws, 'Pallet Weight', tally.PalletWeight, { allowColonVariant: true });

    // ----------------------------
    // Condition of Container — Checkbox in-cell with label
    // ----------------------------
    const conditionOptions: Array<{ key: ConditionOption; label: string }> = [
      { key: 'Condensation', label: 'Condensation' },
      { key: 'Paper Lining', label: 'Paper Lining' },
      { key: 'HoleInContainer', label: 'Hole in Container' },
      { key: 'Other', label: 'Other' },
    ];

    for (const opt of conditionOptions) {
      const isSelected = tally.ConditionOnArrival.includes(opt.key);
      this.checkboxOnLabelCell(ws, opt.label, isSelected, {
        boldSelected: true
      });
    }

    // ----------------------------
    // Insect Activity — Checkbox in-cell with label
    // ----------------------------
    const insectOptions: Array<{ key: InsectOption; label: string }> = [
      { key: 'Larvae', label: 'Larvae' },
      { key: 'Beetles', label: 'Beetles' },
      { key: 'Moths', label: 'Moths' },
      { key: 'Other', label: 'Other' },
    ];

    for (const opt of insectOptions) {
      const isSelected = tally.InsectActivity.includes(opt.key);
      this.checkboxOnLabelCell(ws, opt.label, isSelected, {
        boldSelected: true
      });
    }

    // ----------------------------
    // Full Pallet Weights block
    // ----------------------------
    this.fillTwoBlockGrid(
      ws,
      { weightHeader: 'Weight Including Pallet', bagsHeader: '# of Bags' },
      tally.FullPalletWeights.map(x => ({ weight: x.WeightWithPallets, bags: x.Bags })),
      { rowsPerBlock: 11 }
    );

    // ----------------------------
    // Slack/Damaged Bags block
    // ----------------------------
    this.fillTwoBlockGrid(
      ws,
      { weightHeader: 'Gross Weight of Bag', bagsHeader: '# of Bags' },
      tally.SlackWeights.map(x => ({ weight: x.GrossWeight, bags: x.Bags })),
      { rowsPerBlock: 7 }
    );

    // ----------------------------
    // Test Weights — write value to the LEFT of "Bag 1..6"
    // ----------------------------
    for (const t of tally.TestWeights) {
      const label = `Bag ${t.BagNumber}`;
      const pos = this.findFirstCellByExactText(ws, label);
      if (pos) {
        const leftCol = pos.col - 1;
        if (leftCol >= 1) {
          const target = ws.getCell(pos.row, leftCol);
          this.safeSet(target, t.TestWeight);
        }
      }
    }

    // ----------------------------
    // Tare Weights — write value to the RIGHT of "Bag #1..#3"
    // ----------------------------
    for (const t of tally.TareWeight) {
      const label = `Bag #${t.BagNumber}`;
      const pos = this.findFirstCellByExactText(ws, label);
      if (pos) {
        const rightCol = pos.col + 1;
        const target = ws.getCell(pos.row, rightCol);
        this.safeSet(target, t.TareWeight);
      }
    }

    // 3) Return buffer for caller (download or upload to server)
    const out = await wb.xlsx.writeBuffer();
    if (getPreview) {
      const safeName = `Tally_${(tally.RefPO || 'Sheet').replace(/[^\w\-]+/g, '_')}_${Date.now()}.xlsx`;
      return this.uploadPreviewViaWebService(new Uint8Array(out), safeName);  // returns string URL
    }
    else
      return new Uint8Array(out);
  }

  // ======================================================
  // READ (new)
  // ======================================================
  /** Downloads an existing tally workbook at `path` and parses it back to InboundTallyData. */
  async ReadInboundTallyFromPath(path: string): Promise<InboundTallyData> {
    // 1) download existing Excel
    const blob: Blob = await lastValueFrom(
      this.webService.getWebCall('files', 'download', {
        params: [{ name: 'path', value: path }],
        responseType: 'blob'
      })
    );
    const ab = await blob.arrayBuffer();

    // 2) load workbook
    const wb = new ExcelJS.Workbook();
    await wb.xlsx.load(ab);
    const ws = wb.worksheets[0];

    // 3) headers / crew
    const DateStrippedRaw = this.getRightOfLabel(ws, 'Date Stripped');
    const DateStripped = this.coerceDateOrString(DateStrippedRaw);
    const RefPO = this.getRightOfLabel(ws, 'Ref#/PO#');
    const CoffeeName = this.getRightOfLabel(ws, 'Coffee Description');
    const Marks = this.getRightOfLabel(ws, 'Marks');
    const BagsExpected = this.toInt(this.getRightOfLabel(ws, 'Bags Expected'));
    const ContainerNumber = this.getRightOfLabel(ws, 'Container#');
    const SealNumber = this.getRightOfLabel(ws, 'Seal#');

    const Weigher = this.getRightOfLabel(ws, 'Weigher:', { allowColonVariant: true });
    const Signature = this.getRightOfLabel(ws, 'Signature:', { allowColonVariant: true });
    const HiLoDriver = this.getRightOfLabel(ws, 'HiLo Driver:', { allowColonVariant: true });
    const Helper1 = this.getRightOfLabel(ws, 'Helper #1');
    const Helper2 = this.getRightOfLabel(ws, 'Helper #2');
    const StackingDriver = this.getRightOfLabel(ws, 'Stacking Driver:', { allowColonVariant: true });
    const LocationNumber = this.getRightOfLabel(ws, 'Location #:', { allowColonVariant: true });
    const Comments = this.getRightOfLabel(ws, 'Comments:', { allowColonVariant: true });

    // 4) checkbox groups (match writer labels)
    const ConditionOnArrival = this.readCheckboxGroup(ws, [
      { key: 'Condensation', label: 'Condensation' },
      { key: 'Paper Lining', label: 'Paper Lining' },
      { key: 'HoleInContainer', label: 'Hole in Container' },
      { key: 'Other', label: 'Other' }
    ]) as InboundTallyData['ConditionOnArrival'];

    const InsectActivity = this.readCheckboxGroup(ws, [
      { key: 'Larvae', label: 'Larvae' },
      { key: 'Beetles', label: 'Beetles' },
      { key: 'Moths', label: 'Moths' },
      { key: 'Other', label: 'Other' }
    ]) as InboundTallyData['InsectActivity'];

    // 5) grids: pallets & slacks
    const FullPalletWeights = this.readTwoBlockGrid(
      ws,
      { weightHeader: 'Weight Including Pallet', bagsHeader: '# of Bags' },
      11
    ).map(r => ({
      WeightWithPallets: this.toNumber(r.weight),
      Bags: this.toInt(r.bags),
    })).filter(r => (
      r.Bags > 0 && r.WeightWithPallets > 0
    ));

    const SlackWeights = this.readTwoBlockGrid(
      ws,
      { weightHeader: 'Gross Weight of Bag', bagsHeader: '# of Bags' },
      7
    ).map(r => ({
      GrossWeight: this.toNumber(r.weight),
      Bags: this.toInt(r.bags),
    })).filter(r => (
      r.Bags > 0 && r.GrossWeight > 0
    ))

    // 6) test weights (value immediately to LEFT of "Bag 1..6")
    const TestWeights = [1, 2, 3, 4, 5, 6].map(n => {
      const pos = this.findFirstCellByExactText(ws, `Bag ${n}`);
      if (!pos) return null;
      const leftCol = pos.col - 1;
      if (leftCol < 1) return null;
      const val = this.getCellValue(ws.getCell(pos.row, leftCol));
      const num = this.toNumber(val);
      return Number.isFinite(num) ? { TestWeight: num, BagNumber: n as BagNum } : null;
    }).filter(Boolean) as InboundTallyData['TestWeights'];

    // 7) tare weights (value immediately to RIGHT of "Bag #1..#3")
    const TareWeight = [1, 2, 3].map(n => {
      const pos = this.findFirstCellByExactText(ws, `Bag #${n}`);
      if (!pos) return null;
      const rightCol = pos.col + 1;
      const val = this.getCellValue(ws.getCell(pos.row, rightCol));
      const num = this.toNumber(val);
      return Number.isFinite(num) ? { TareWeight: num, BagNumber: n as 1 | 2 | 3 } : null;
    }).filter(Boolean) as InboundTallyData['TareWeight'];

    // 8) Pallet weight (only if you store it somewhere as a labeled value; default 0)

    const PalletWeight = this.toNumber(
      this.getRightOfLabel(ws, 'Pallet Weight', { allowColonVariant: true })
    );


    // 9) final payload
    const data: InboundTallyData = {
      FullPalletWeights,
      SlackWeights,
      TestWeights,

      LocationNumber,
      Comments: Comments || '',
      DateStripped: (DateStripped instanceof Date)
        ? DateStripped.toISOString().substring(0, 10)
        : (String(DateStripped || '')),

      RefPO: RefPO || '',
      CoffeeName: CoffeeName || '',
      Marks: Marks || '',
      BagsExpected: BagsExpected || 0,
      ContainerNumber: ContainerNumber || '',
      SealNumber: SealNumber || '',

      ConditionOnArrival,
      InsectActivity,

      PalletWeight,
      TareWeight,

      Weigher: Weigher || '',
      Signature: Signature || '',
      HiLoDriver: HiLoDriver || '',
      Helper1: Helper1 || '',
      Helper2: Helper2 || '',
      StackingDriver: StackingDriver || ''
    };

    return data;
  }

  // ======================================================
  // Helpers (shared)
  // ======================================================

  /** Place value into the cell to the RIGHT of a label cell matched by text (case-insensitive). */
  private setRightOfLabel(
    ws: ExcelJS.Worksheet,
    label: string,
    value: any,
    opts?: { dateFmt?: string; allowColonVariant?: boolean }
  ) {
    const pos = this.findFirstCellByExactText(ws, label, { allowColonVariant: !!opts?.allowColonVariant });
    if (!pos) return;
    const cell = ws.getCell(pos.row, pos.col + 1);
    if (value instanceof Date) {
      this.safeSet(cell, value);
      if (opts?.dateFmt) cell.numFmt = opts.dateFmt;
    } else {
      this.safeSet(cell, value);
    }
  }

  /** Read value from the cell RIGHT of a label cell. */
  private getRightOfLabel(
    ws: ExcelJS.Worksheet,
    label: string,
    opts?: { allowColonVariant?: boolean }
  ): any {
    const pos = this.findFirstCellByExactText(ws, label, { allowColonVariant: !!opts?.allowColonVariant });
    if (!pos) return '';
    const cell = ws.getCell(pos.row, pos.col + 1);
    return this.getCellValue(cell);
  }

  /** Render a Unicode checkbox + label in the same cell that already holds the label. */
  private checkboxOnLabelCell(
    ws: ExcelJS.Worksheet,
    labelText: string,
    isSelected: boolean,
    opts?: { boldSelected?: boolean; highlightArgb?: string }
  ) {
    const pos = this.findFirstCellByExactText(ws, labelText);
    if (!pos) return;

    const cell = ws.getCell(pos.row, pos.col);

    (cell.value as any) = {
      richText: [
        { text: `${isSelected ? '☑' : '☐'} `, font: { name: 'Segoe UI Symbol', size: 12 } },
        { text: labelText, font: { bold: !!opts?.boldSelected && isSelected } }
      ]
    };

    cell.alignment = { vertical: 'middle', horizontal: 'left' };

    if (isSelected && opts?.highlightArgb) {
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: opts.highlightArgb } };
    }
  }

  /** Fill a 2-block grid under repeated headers. */
  private fillTwoBlockGrid(
    ws: ExcelJS.Worksheet,
    headers: { weightHeader: string; bagsHeader: string },
    rows: Array<{ weight: number; bags: number }>,
    opts: { rowsPerBlock: number }
  ) {
    const weightCells = this.findAllCellsByExactText(ws, headers.weightHeader);
    if (weightCells.length < 2) return; // Need two blocks
    weightCells.sort((a, b) => a.col - b.col);

    const left = weightCells[0];
    const right = weightCells[1];

    const leftWeightCol = left.col;
    const leftBagsCol = left.col + 1;
    const rightWeightCol = right.col;
    const rightBagsCol = right.col + 1;

    const leftStartRow = left.row + 1;
    const rightStartRow = right.row + 1;

    for (let i = 0; i < Math.min(rows.length, opts.rowsPerBlock); i++) {
      const r = leftStartRow + i;
      this.safeSet(ws.getCell(r, leftWeightCol), rows[i].weight);
      this.safeSet(ws.getCell(r, leftBagsCol), rows[i].bags);
    }

    for (let i = opts.rowsPerBlock; i < rows.length; i++) {
      const r = rightStartRow + (i - opts.rowsPerBlock);
      this.safeSet(ws.getCell(r, rightWeightCol), rows[i].weight);
      this.safeSet(ws.getCell(r, rightBagsCol), rows[i].bags);
    }
  }

  /** Read a 2-block grid back to rows. */
  private readTwoBlockGrid(
    ws: ExcelJS.Worksheet,
    headers: { weightHeader: string; bagsHeader: string },
    rowsPerBlock: number
  ): Array<{ weight: number; bags: number }> {
    const lefts = this.findAllCellsByExactText(ws, headers.weightHeader);
    if (lefts.length < 2) return [];
    lefts.sort((a, b) => a.col - b.col);
    const left = lefts[0], right = lefts[1];

    const leftWeightCol = left.col, leftBagsCol = left.col + 1;
    const rightWeightCol = right.col, rightBagsCol = right.col + 1;
    const leftStartRow = left.row + 1, rightStartRow = right.row + 1;

    const rows: Array<{ weight: number; bags: number }> = [];

    for (let i = 0; i < rowsPerBlock; i++) {
      const rw = leftStartRow + i;
      const w = this.toNumber(this.getCellValue(ws.getCell(rw, leftWeightCol)));
      const b = this.toInt(this.getCellValue(ws.getCell(rw, leftBagsCol)));
      if (Number.isFinite(w) || b > 0) rows.push({ weight: w, bags: b });
    }

    for (let i = 0; i < rowsPerBlock; i++) {
      const rw = rightStartRow + i;
      const w = this.toNumber(this.getCellValue(ws.getCell(rw, rightWeightCol)));
      const b = this.toInt(this.getCellValue(ws.getCell(rw, rightBagsCol)));
      if (Number.isFinite(w) || b > 0) rows.push({ weight: w, bags: b });
    }

    return rows.filter(r => Number.isFinite(r.weight) || r.bags > 0);
  }

  /** Try to parse date string -> Date; if fail, return original string. */
  private asDateValue(d: string): Date | string {
    const dt = new Date(d);
    return isNaN(dt.getTime()) ? d : dt;
  }

  /** Write unless it looks like a formula cell. */
  private safeSet(cell: ExcelJS.Cell, value: any) {
    if (cell.value && typeof cell.value === 'object' && 'formula' in (cell.value as any)) return;
    cell.value = value;
  }

  /** Find first cell that matches label (case-insensitive). Optionally allow colon variants. */
  private findFirstCellByExactText(
    ws: ExcelJS.Worksheet,
    label: string,
    opts?: { allowColonVariant?: boolean }
  ): { row: number; col: number } | null {
    const targets = [this.norm(label)];
    if (opts?.allowColonVariant) {
      targets.push(this.norm(label.replace(/:$/, '')), this.norm(label + ':'), this.norm(label.replace(/:?$/, ':')));
    }
    let hit: { row: number; col: number } | null = null;
    ws.eachRow({ includeEmpty: false }, (row, r) => {
      row.eachCell({ includeEmpty: false }, (cell, c) => {
        const text = this.cellVisibleText(cell);
        if (targets.includes(this.norm(text)) && !hit) {
          hit = { row: r, col: c };
        }
      });
    });
    return hit;
  }

  /** Find all cells whose visible text equals the label. */
  private findAllCellsByExactText(
    ws: ExcelJS.Worksheet,
    label: string
  ): Array<{ row: number; col: number }> {
    const target = this.norm(label);
    const hits: Array<{ row: number; col: number }> = [];
    ws.eachRow({ includeEmpty: false }, (row, r) => {
      row.eachCell({ includeEmpty: false }, (cell, c) => {
        if (this.norm(this.cellVisibleText(cell)) === target) hits.push({ row: r, col: c });
      });
    });
    return hits;
  }

  private cellVisibleText(cell: ExcelJS.Cell): string {
    const v: any = cell.value;
    if (!v) return '';
    if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') return String(v);
    if (typeof v === 'object') {
      if ('richText' in v && Array.isArray(v.richText)) return v.richText.map((rt: any) => rt.text ?? '').join('');
      if ('result' in v && v.result != null) return String(v.result);
      if ('formula' in v) return ''; // don’t use formula text for matching
    }
    return '';
  }

  private norm(s: any): string {
    return String(s ?? '').replace(/\s+/g, ' ').trim().toLowerCase();
  }

  /** Read the value in a cell in a robust way (number/string/richText/result). */
  private getCellValue(cell: ExcelJS.Cell): any {
    const v: any = cell.value;
    if (!v) return '';
    if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') return v;
    if (typeof v === 'object') {
      if ('result' in v && v.result != null) return v.result;
      if ('richText' in v && Array.isArray(v.richText)) return v.richText.map((rt: any) => rt.text ?? '').join('');
      if ('formula' in v) return ''; // ignore raw formula content
    }
    return '';
  }

  private toNumber(v: any): number {
    const n = (typeof v === 'number') ? v : Number(String(v).replace(/[, ]+/g, ''));
    return Number.isFinite(n) ? n : NaN;
  }
  private toInt(v: any): number {
    const n = parseInt(String(v).replace(/[^\d\-]+/g, ''), 10);
    return Number.isFinite(n) ? n : 0;
  }
  private coerceDateOrString(v: any): Date | string {
    if (v instanceof Date) return v;
    const d = new Date(v);
    return isNaN(d.getTime()) ? String(v ?? '') : d;
  }

  /** Read ☑/☐ rich text checkboxes written by checkboxOnLabelCell */
  private readCheckboxGroup(
    ws: ExcelJS.Worksheet,
    items: Array<{ key: string; label: string }>
  ): string[] {
    const out: string[] = [];
    for (const it of items) {
      const pos = this.findFirstCellByExactText(ws, it.label);
      if (!pos) continue;
      const cell = ws.getCell(pos.row, pos.col);
      const val: any = cell.value;
      let checked = false;
      if (val && typeof val === 'object' && 'richText' in val && Array.isArray(val.richText)) {
        checked = (val.richText[0]?.text ?? '').includes('☑');
      } else {
        checked = String(this.getCellValue(cell)).trim().startsWith('☑');
      }
      if (checked) out.push(it.key);
    }
    return out;
  }

  // ======================================================
  // PREVIEW upload (existing)
  // ======================================================
  async uploadPreviewViaWebService(bytes: Uint8Array, fileName: string): Promise<string> {
    const fileBlob = new Blob([bytes], { type: this.EXCEL_MIME });
    const form = new FormData();
    form.append('file', fileBlob, fileName);

    const resp = await lastValueFrom(
      this.webService.getWebCall('files', 'uploadForPreview', { body: form })
    );

    return resp?.url ?? '';
  }
  
  // ====== Add these helpers inside ExcelWriterService class ======

  /** Excel serial date -> JS Date. Supports 1900/1904 date systems. */
  private fromExcelSerial(serial: number, date1904: boolean): Date {
    // Excel day 1 (1900 system) = 1899-12-31; day 60 is the fake 1900-02-29
    const excelEpoch = date1904 ? new Date(Date.UTC(1904, 0, 1))  // 1904-01-01
      : new Date(Date.UTC(1899, 11, 30)); // 1899-12-30 (Excel's 0)
    let days = serial;

    if (!date1904) {
      // Adjust for Excel's 1900 leap-year bug: remove the nonexistent 1900-02-29
      if (serial >= 60) days -= 1;
    }

    const ms = days * 24 * 60 * 60 * 1000;
    return new Date(excelEpoch.getTime() + ms);
  }
}
